import { useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { Button, Input, Modal } from '@react-apps/design-system';
import {
  useCreateUser,
  useProvisionScopedUser,
} from '../hooks/use-create-user';
import { useScopes } from '@/shared/scopes/use-scopes';
import type { Scope } from '@/shared/scopes/scopes-api.datasource';
import type { ProvisionResult } from '../../domain/entities/user.entity';
import {
  accessGrantedMessage,
  accessGrantErrorMessage,
} from '../access-grant-outcome';
import { ScopeSelect } from './ScopeSelect';

interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * As plataformas que o iamEditor administra (`useProvisionableScopes`, que
   * roda na página). Presente, o modal está no modo dele: scope obrigatório,
   * só da lista, e a rota única. Ausente, é o admin, com a lista inteira.
   */
  provisionableScopes?: UseQueryResult<Scope[]>;
}

/** Mensagem por desfecho — conta nova ou já existente, com acesso ou sem. */
function resultMessage(r: ProvisionResult): string {
  if (r.created && r.accessGranted) {
    return 'Conta criada com acesso à plataforma. Enviamos um e-mail para a pessoa definir a própria senha.';
  }
  if (r.created) {
    return 'Conta criada. Enviamos um e-mail para a pessoa definir a própria senha.';
  }
  if (r.accessGranted) {
    return 'Este e-mail já estava cadastrado — demos o acesso à plataforma. Quem ainda não definiu a senha recebe o convite; quem já definiu entra direto.';
  }
  return 'Este e-mail já está cadastrado. Nenhum convite foi enviado.';
}

export function CreateUserModal({
  open,
  onClose,
  provisionableScopes,
}: CreateUserModalProps) {
  const scoped = provisionableScopes !== undefined;
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [scopeId, setScopeId] = useState('');
  const admin = useCreateUser();
  const iamEditor = useProvisionScopedUser();
  const allScopes = useScopes(!scoped);
  const mutation = scoped ? iamEditor : admin;
  const scopeSource = provisionableScopes ?? allScopes;

  const close = () => {
    admin.reset();
    iamEditor.reset();
    setEmail('');
    setName('');
    setScopeId('');
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const input = { email: email.trim(), name: name.trim() };
    if (scoped) iamEditor.mutate({ ...input, scopeId });
    else admin.mutate({ ...input, scopeId: scopeId || undefined });
  };

  const canSubmit =
    email.trim().length > 0 &&
    name.trim().length > 0 &&
    (!scoped || (scopeId.length > 0 && !scopeSource.isError)) &&
    !mutation.isPending;

  const successMessage = scoped
    ? iamEditor.data && accessGrantedMessage(iamEditor.data.profileCreated)
    : admin.data && resultMessage(admin.data);

  return (
    <Modal
      open={open}
      onClose={close}
      title="Criar usuário"
      description={
        scoped
          ? 'Você só pode dar acesso às plataformas que administra.'
          : 'Provisiona uma conta sem senha. A pessoa recebe um e-mail para definir a própria senha.'
      }
    >
      {mutation.isSuccess && successMessage ? (
        <div className="space-y-4">
          <p role="status" className="text-(--color-text-secondary) text-sm">
            {successMessage}
          </p>
          <div className="flex justify-end pt-2">
            <Button type="button" onClick={close}>
              Fechar
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="E-mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Input
            label="Nome"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <ScopeSelect
            label={scoped ? 'Dar acesso a' : 'Dar acesso a (opcional)'}
            value={scopeId}
            onChange={setScopeId}
            source={scopeSource}
            emptyPlaceholder={
              scoped ? 'Selecione uma plataforma…' : 'Nenhuma por enquanto'
            }
          />
          {!scoped && (
            <p className="text-(--color-text-secondary) text-xs">
              Escolher aqui cria o perfil de plataforma no mesmo ato e manda um
              convite só. Deixando em branco, a pessoa recebe o convite agora e
              o acesso pode ser dado depois, na página dela.
            </p>
          )}
          {mutation.error && (
            <p role="alert" className="text-sm text-red-600">
              {accessGrantErrorMessage(mutation.error)}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={close}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {mutation.isPending ? 'Criando…' : 'Criar'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
